<template>
    <div class="h-input relative flex w-full max-w-full flex-col">
        <label
            v-if="label"
            class="-ml-3 pb-4 text-xs"
        >
            {{ label }}
        </label>
        <SliderRoot
            v-model="sliderValues"
            class="relative my-auto flex h-8 w-full touch-none items-center select-none"
            :step="step"
            :min="sliderMin"
            :max="sliderMax"
            :aria-label="label || vid"
        >
            <SliderTrack class="relative h-1.5 grow overflow-hidden rounded-sm bg-gray-300">
                <SliderRange
                    class="absolute h-full"
                    :class="errorMessage ? 'bg-error' : 'bg-brand'"
                />
            </SliderTrack>
            <SliderThumb
                v-for="(_, index) in sliderValues"
                :key="index"
                class="group shadow-slider active:shadow-slider-active focus-visible:ring-brand/30 relative block size-8 cursor-grab rounded-full border-0 bg-white focus-visible:ring-3 focus-visible:outline-hidden active:cursor-grabbing"
                :aria-label="thumbLabel(index)"
            >
                <span
                    class="pointer-events-none absolute bottom-9 left-1/2 min-w-5 -translate-x-1/2 rounded border px-1.5 py-1 text-center text-sm font-semibold whitespace-nowrap text-white after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-4 after:border-transparent after:content-['']"
                    :class="[
                        errorMessage ?
                            'border-error bg-error after:border-t-error'
                        :   'border-brand bg-brand after:border-t-brand',
                        showToolTipAlways ? 'block' : 'hidden group-focus:block group-active:block',
                    ]"
                >
                    {{ sliderValues[index] }}
                </span>
            </SliderThumb>
        </SliderRoot>
        <div
            class="text-xxs pointer-events-none absolute -bottom-5 left-0 w-full truncate whitespace-nowrap"
            :class="errorMessage ? 'text-error' : ''"
        >
            <span class="text-xxs ml-2">{{ errorMessage }}</span>
        </div>
        <div
            v-if="boundInputs"
            class="absolute -bottom-10 flex w-full justify-between"
        >
            <MuvalInput
                v-model="sliderBoxMin"
                label="Min space"
                infoType="m³"
                class="w-36"
            />
            <MuvalInput
                v-model="sliderBoxMax"
                label="Max space"
                infoType="m³"
                class="w-36"
            />
        </div>
    </div>
</template>

<script setup>
import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from 'reka-ui';
import MuvalInput from '@components/inputs/MuvalInput.vue';
import { useField } from 'vee-validate';
import { idGen } from '@utilities/idGen.js';

const props = defineProps({
    modelValue: {
        type: [Number, Array, null],
        required: false,
        default: null,
    },
    // Retained for backwards compatibility with the shared input API.
    selectOptions: {
        type: [Array, null],
        required: false,
        default: null,
    },
    label: {
        type: [String, null],
        required: false,
        default: null,
    },
    min: {
        type: Number,
        required: false,
        default: 0,
    },
    max: {
        type: Number,
        required: false,
        default: 100,
    },
    vid: {
        type: String,
        required: false,
        default: () => idGen(6),
    },
    boundInputs: {
        type: Boolean,
        required: false,
        default: false,
    },
    showToolTipAlways: {
        type: Boolean,
        required: false,
        default: true,
    },
    step: {
        type: Number,
        required: false,
        default: 1,
    },
});

const emit = defineEmits(['update:modelValue']);

const toNumber = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

const valuesEqual = (left, right) => {
    const leftValues = Array.isArray(left) ? left : [left];
    const rightValues = Array.isArray(right) ? right : [right];

    return leftValues.length === rightValues.length && leftValues.every((value, index) => value === rightValues[index]);
};

const fieldOptions = props.modelValue === null ? undefined : { initialValue: props.modelValue };
const { value: inputValue, errorMessage } = useField(props.vid, undefined, fieldOptions);

const sliderBox = ref({
    min: props.min,
    max: props.max,
});

const sliderBoxMin = computed({
    get: () => sliderBox.value.min,
    set: (value) => {
        const number = toNumber(value, 0);
        sliderBox.value.min = number >= 0 && number < sliderBox.value.max ? number : 0;
    },
});

const sliderBoxMax = computed({
    get: () => sliderBox.value.max,
    set: (value) => {
        const number = toNumber(value, sliderBox.value.max);
        sliderBox.value.max =
            number >= 0 && number > sliderBox.value.min ? number
            : sliderBox.value.min < 100 ? 100
            : sliderBox.value.min + 1;
    },
});

const sliderMin = computed(() => (props.boundInputs ? sliderBox.value.min : props.min));
const sliderMax = computed(() => (props.boundInputs ? sliderBox.value.max : props.max));
const usesRangeValue = computed(() => Array.isArray(props.modelValue) || Array.isArray(inputValue.value));

const sliderValues = computed({
    get: () => {
        const values = Array.isArray(inputValue.value) ? inputValue.value : [inputValue.value];
        return values.length ? values.map((value) => toNumber(value, sliderMin.value)) : [sliderMin.value];
    },
    set: (values) => {
        const normalisedValues = (values ?? [sliderMin.value]).map((value) => toNumber(value, sliderMin.value));
        inputValue.value = usesRangeValue.value ? normalisedValues : normalisedValues[0];
    },
});

const thumbLabel = (index) => {
    if (sliderValues.value.length === 1) {
        return props.label || props.vid;
    }

    return `${props.label || props.vid} ${index === 0 ? 'minimum' : 'maximum'}`;
};

watch(
    inputValue,
    (newValue) => {
        const output = Array.isArray(newValue) ? [...newValue] : toNumber(newValue, sliderMin.value);

        if (!valuesEqual(output, props.modelValue)) {
            emit('update:modelValue', output);
        }
    },
    { deep: true },
);

watch(
    () => props.modelValue,
    (newModel) => {
        const nextValue =
            Array.isArray(newModel) ?
                newModel.map((value) => toNumber(value, sliderMin.value))
            :   toNumber(newModel, sliderMin.value);

        if (!valuesEqual(nextValue, inputValue.value)) {
            inputValue.value = nextValue;
        }
    },
    { deep: true },
);
</script>
